import { PageHero } from "../components/common/PageHero";
import { SectionHeading } from "../components/common/SectionHeading";
import { ManpowerForm } from "../components/forms/ManpowerForm";
import { ConstructionForm } from "../components/forms/ConstructionForm";
import { ContactForm } from "../components/forms/ContactForm";
import { Reveal } from "../components/common/Reveal";
import { CTAButton } from "../components/common/CTAButton";
import { site, images } from "../data/content";
import { Phone, EnvelopeSimple, WhatsappLogo, MapPin, Globe, Lightning } from "@phosphor-icons/react";

const Cards = () => {
    const all = [
        site.phone && { icon: Phone, label: "Call Us", value: site.phone, href: `tel:${site.phoneRaw}`, testid: "contact-call" },
        site.whatsapp && { icon: WhatsappLogo, label: "WhatsApp", value: site.phone || "Chat with us", href: `https://wa.me/${site.whatsapp}`, testid: "contact-whatsapp" },
        { icon: EnvelopeSimple, label: "Email", value: site.email, href: `mailto:${site.email}`, testid: "contact-email" },
        { icon: Globe, label: "Website", value: site.website, href: site.websiteUrl, testid: "contact-website" },
    ].filter(Boolean);
    return (
        <div className="grid sm:grid-cols-2 gap-4">
            {all.map((c) => (
                <a key={c.label} href={c.href} target={c.href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid={c.testid}
                   className="group p-5 rounded-sm border border-white/10 bg-white/5 hover:border-brand transition-colors">
                    <c.icon size={26} weight="duotone" className="text-brand" />
                    <div className="mt-3 text-xs uppercase tracking-wider text-slate-400">{c.label}</div>
                    <div className="mt-1 text-sm font-semibold text-white break-all">{c.value}</div>
                </a>
            ))}
        </div>
    );
};

export default function Contact() {
    return (
        <>
            <PageHero
                overline="Contact Us"
                breadcrumb="Contact"
                title="Let's discuss your manpower or project needs"
                subtitle="Reach out for workforce requirements, construction projects or partnerships. Our team responds promptly across India."
                image={images.corporate}
            />

            <section className="bg-navy-deep text-white">
                <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-12 gap-12 items-start">
                    <div className="lg:col-span-5">
                        <h2 className="font-display font-extrabold text-2xl lg:text-3xl">{site.name}</h2>
                        <p className="mt-3 flex items-center gap-2 text-slate-300 text-sm"><MapPin size={18} className="text-brand" /> {site.address}</p>
                        <p className="mt-1 text-xs text-slate-500">CIN: {site.cin}</p>
                        <div className="mt-8"><Cards /></div>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <CTAButton href="#manpower" variant="primary" arrow={false} className="!px-5 !py-3">Request Manpower</CTAButton>
                            <CTAButton href="#construction" variant="outline" arrow={false} className="!px-5 !py-3">Get Construction Quote</CTAButton>
                            <CTAButton href="#general" variant="outline" arrow={false} className="!px-5 !py-3">Partner With Us</CTAButton>
                        </div>
                    </div>
                    <div className="lg:col-span-7 w-full">
                        <div className="rounded-sm overflow-hidden border border-white/10 h-[320px] lg:h-[440px]">
                            <iframe
                                title="FRONTX location"
                                data-testid="contact-map"
                                width="100%"
                                height="100%"
                                loading="lazy"
                                style={{ border: 0, filter: "grayscale(0.3) invert(0.9) hue-rotate(180deg)" }}
                                referrerPolicy="no-referrer-when-downgrade"
                                src={`https://www.google.com/maps?q=${encodeURIComponent(site.mapQuery)}&output=embed`}
                            />
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-[#f8fafc]" id="manpower" data-testid="manpower-section">
                <div className="mx-auto max-w-[1100px] px-5 lg:px-10 py-24 lg:py-32">
                    <SectionHeading overline="Manpower Requirement" title="Tell Us Your Manpower Requirement" subtitle="Share your requirement and our team will connect with the right workforce plan." align="center" />
                    <div className="mt-6 flex items-center justify-center gap-2 text-sm text-navy font-semibold">
                        <Lightning size={18} weight="fill" className="text-brand" />
                        Need urgent manpower? <a href={`mailto:${site.email}`} className="text-brand underline">Contact our team.</a>
                    </div>
                    <Reveal>
                        <div className="mt-10 bg-white border border-slate-200 rounded-sm p-6 lg:p-10">
                            <ManpowerForm />
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="bg-white" id="construction" data-testid="construction-section">
                <div className="mx-auto max-w-[1100px] px-5 lg:px-10 py-24 lg:py-32">
                    <SectionHeading overline="Construction Enquiry" title="Get a Project Quote" subtitle="Describe your construction project and we'll respond with a tailored proposal." align="center" />
                    <Reveal>
                        <div className="mt-10 bg-[#f8fafc] border border-slate-200 rounded-sm p-6 lg:p-10">
                            <ConstructionForm />
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="bg-[#f8fafc]" id="general" data-testid="general-section">
                <div className="mx-auto max-w-[900px] px-5 lg:px-10 py-24 lg:py-32">
                    <SectionHeading overline="General Enquiry" title="Partner With Us" subtitle="Have a question or a partnership proposal? Send us a message." align="center" />
                    <Reveal>
                        <div className="mt-10 bg-white border border-slate-200 rounded-sm p-6 lg:p-10">
                            <ContactForm />
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
